import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentRegionName } from "@/apps/goals/hooks/useCurrentRegionName";
import Typography from "@/ui/Typography/Typography";
import { ChevronDownIcon, GiftIcon } from "./icons";
import { useRafflePageContext } from "./RafflePageContext";
import cls from "./styles.module.scss";
import { formatMoney, RUBLE_SIGN } from "./utils";

const useRaffleLiveState = (
  liveSeconds: number | null | undefined,
  resultsSeconds: number | null
) => {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    setElapsedSeconds(0);

    if (!liveSeconds || liveSeconds <= 0 || resultsSeconds === null) {
      return undefined;
    }

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      const nextElapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
      setElapsedSeconds(nextElapsedSeconds);

      if (nextElapsedSeconds >= liveSeconds) {
        window.clearInterval(intervalId);
      }
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [liveSeconds, resultsSeconds]);

  if (!liveSeconds || liveSeconds <= 0 || resultsSeconds === null) {
    return false;
  }

  const remainingResultsSeconds = Math.max(0, resultsSeconds - elapsedSeconds);

  return remainingResultsSeconds <= 60 && elapsedSeconds < liveSeconds;
};

export const PrizeSummaryCard = () => {
  const { t } = useTranslation();
  const { raffle, participants, resultsSeconds } = useRafflePageContext();
  const regionName = useCurrentRegionName();
  const [isPrizesOpen, setIsPrizesOpen] = useState(false);

  const prizePlaces = raffle.prize_places;
  const isLive = useRaffleLiveState(raffle.live_seconds, resultsSeconds);
  const winners = participants.filter(
    (participant) => participant.is_winner && participant.prize_amount !== null
  );
  const isFinishedRaffle = Boolean(raffle.drawn_at);
  const prizeRows = isFinishedRaffle
    ? winners.map((winner) => ({
        id: `${winner.rank}-${winner.name}`,
        label: winner.name,
        amount: winner.prize_amount ?? 0,
      }))
    : prizePlaces.map((prize) => ({
        id: String(prize.place),
        label: t("raffle.prizes.place", { place: prize.place }),
        amount: prize.amount,
      }));
  const prizeCount = prizeRows.length || raffle.prize_count;

  return (
    <div className={cls.summaryCard}>
      {(regionName || isLive) && (
        <div className={cls.summaryTopLine}>
          {regionName && (
            <Typography className={cls.summaryRegion} size="sm" weight="medium">
              {regionName}
            </Typography>
          )}
          {isLive && (
            <span className={cls.summaryLive}>
              <span className={cls.summaryLiveDot} />
              {t("raffle.prizes.live")}
            </span>
          )}
        </div>
      )}
      <button
        type="button"
        className={cls.summaryToggle}
        onClick={() => setIsPrizesOpen((current) => !current)}
        aria-expanded={isPrizesOpen}
      >
        <div className={cls.summaryIcon}>
          <GiftIcon />
        </div>

        <div className={cls.summaryMain}>
          <Typography className={cls.summaryTitle} size="sm" weight="medium">
            {t("raffle.prizes.title")}
          </Typography>
          <Typography
            className={cls.summaryAmount}
            component="div"
            size="2xl"
            weight="bold"
          >
            {formatMoney(raffle.prize_fund)} {RUBLE_SIGN}
          </Typography>
        </div>
        <div className={cls.summaryMeta}>
          <Typography
            className={cls.summaryCount}
            component="div"
            size="sm"
            weight="semibold"
          >
            {t("raffle.prizes.count", { count: prizeCount })}
          </Typography>
          <div
            className={[
              cls.summaryChevron,
              isPrizesOpen ? cls.summaryChevronOpen : "",
            ].join(" ")}
          >
            <ChevronDownIcon />
          </div>
        </div>
      </button>
      <div
        className={[
          cls.prizesPanel,
          isPrizesOpen ? cls.prizesPanelOpen : "",
        ].join(" ")}
        aria-hidden={!isPrizesOpen}
      >
        <div className={cls.prizesList}>
          {prizeRows.map((prize) => (
            <div key={prize.id} className={cls.prizeRow}>
              <Typography
                className={cls.prizePlace}
                component="div"
                size="md"
                weight="medium"
              >
                {prize.label}
              </Typography>
              <Typography
                className={cls.prizeAmount}
                component="div"
                size="md"
                weight="bold"
              >
                {formatMoney(prize.amount)} {RUBLE_SIGN}
              </Typography>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
